import React, { useState, useEffect, useRef } from 'react';
import Comments from './Comments';
import { fetchReactions } from '../../../api/reactions';
import { fetchRepostCount, addRepost, fetchShareCount, addShare } from '../../../api/postActions';
import Modal from 'react-modal';
import './ForumTab.css';
import '../../../styles/social-posts.css';
import { FaThumbsUp, FaHeart, FaLaugh, FaAngry, FaSadTear, FaPaperclip, FaPaperPlane, FaRetweet, FaRegCommentDots, FaShareAlt, FaLink, FaExternalLinkAlt, FaEllipsisV, FaEdit, FaTrash } from 'react-icons/fa';
import { extractEmbedFromContent, getEmbedSrc } from '../../../utils/embed';
import PostReactions from '../../Home/Components/PostReactions';
import HoverCard from '../../../components/HoverCard';
import DockedComposer from '../../../components/DockedComposer';

const reactionOptions = [
  { type: 'like', label: '👍' },
  { type: 'love', label: '❤️' },
  { type: 'funny', label: '😂' },
  { type: 'wow', label: '😮' },
  { type: 'sad', label: '😢' },
  { type: 'clap', label: '👏' }
];

const ForumTab = ({ groupId, groupMembers, userProfile, supabase, postId }) => {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [postMedia, setPostMedia] = useState(null);
  const [postMediaPreview, setPostMediaPreview] = useState(null);
  const [localEmbedPreview, setLocalEmbedPreview] = useState(null);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);
  const [embedMenuOpen, setEmbedMenuOpen] = useState({});
  const embedMenuRef = useRef();
  const [reactionsMap, setReactionsMap] = useState({});
  const [userReactions, setUserReactions] = useState({});
  const [repostCounts, setRepostCounts] = useState({});
  const [shareCounts, setShareCounts] = useState({});
  const [commentCounts, setCommentCounts] = useState({});
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);
  const [replyToCommentId, setReplyToCommentId] = useState(null);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const fileInputRef = useRef();
  const [editingPostId, setEditingPostId] = useState(null);
  const [editedPostContent, setEditedPostContent] = useState('');
  const [showReactions, setShowReactions] = useState({});
  const [pickerPostId, setPickerPostId] = useState(null);
  const [pickerVisible, setPickerVisible] = useState(false);
  const pickerRef = useRef();
  const [ownerMenuOpen, setOwnerMenuOpen] = useState({});

  // Shared reaction picker auth headers
  const getAuthHeaders = async () => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token || '';
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line
  }, [groupId]);

  // Close embed menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (embedMenuRef.current && !embedMenuRef.current.contains(e.target)) {
        setEmbedMenuOpen({});
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleEmbedMenu = (itemId) => {
    setEmbedMenuOpen(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const getEmbedPlatform = (url) => {
    if (/youtube|youtu\.be/i.test(url)) return 'YouTube';
    if (/soundcloud/i.test(url)) return 'SoundCloud';
    return 'Link';
  };

  const copyEmbedLink = (url) => {
    navigator.clipboard?.writeText(url);
    setEmbedMenuOpen({});
  };

  const viewOnPlatform = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setEmbedMenuOpen({});
  };

  // Scroll to post if postId is provided
  useEffect(() => {
    if (postId && posts.length > 0) {
      const el = document.getElementById(`forum-post-${postId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-post');
        setTimeout(() => el.classList.remove('highlight-post'), 2000);
      }
    }
  }, [postId, posts]);
  async function fetchPosts() {
    if (!groupId) {
      setPosts([]);
      return;
    }
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token || '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const userId = userProfile?.id || '';
      const res = await fetch(`/api/community?includeReactions=true&groupId=${groupId}&userId=${userId}`, { headers });
      if (!res.ok) throw new Error(`Failed to load posts: ${res.status}`);
      const { posts: serverPosts } = await res.json();
      // Map server format to local format and fetch profiles
      const userIds = [...new Set((serverPosts || []).map(p => p.author?.id || p.user_id).filter(Boolean))];
      let profilesMap = {};
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase.from('profiles').select('id, display_name, avatar_url').in('id', userIds);
        profilesMap = (profilesData || []).reduce((acc, profile) => { acc[profile.id] = profile; return acc; }, {});
      }
      const mapped = (serverPosts || []).map(p => ({
        id: p._id,
        content: p.content,
        media_url: p.media_url || null,
        media_type: p.media_type || null,
        embed_url: p.embed_url || null,
        created_at: p.createdAt,
        group_id: groupId,
        user_id: p.author?.id || p.user_id,
        profile: profilesMap[p.author?.id || p.user_id] || null,
        author: p.author || null,
        reactionsByType: p.reactionsByType || {},
        userReactionType: p.userReactionType || null,
      }));
      setPosts(mapped);
    } catch (err) {
      console.error('Failed to load posts:', err);
      setPosts([]);
    }
  }

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    const raw = newPost.trim();
    const { content, embedUrl } = extractEmbedFromContent(raw);
    const embed_url = embedUrl || null;
    if (!content && !postMedia && !embed_url) return;
    setIsSubmittingPost(true);
    try {
      let uploadedUrl = null;
      let uploadedType = null;
      if (postMedia) {
        const ext = postMedia.name.split('.').pop();
        const filePath = `posts/${userProfile.id}/${Date.now()}_${Math.random().toString(36).substr(2, 8)}.${ext}`;
        let { error: uploadError } = await supabase.storage
          .from('post-media')
          .upload(filePath, postMedia, { cacheControl: '3600', upsert: false });
        if (uploadError) {
          alert('Failed to upload media');
          setIsSubmittingPost(false);
          return;
        }
        const { data: publicUrlData } = supabase
          .storage
          .from('post-media')
          .getPublicUrl(filePath);
        uploadedUrl = publicUrlData.publicUrl;
        uploadedType = postMedia.type;
      }

      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token || '';
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

      const res = await fetch('/api/community', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: content || '',
          groupId,
          media_url: uploadedUrl || undefined,
          media_type: uploadedType || undefined,
          embed_url: embed_url || undefined
        }),
      });

      if (!res.ok) throw new Error(`Post failed: ${res.status}`);

      setNewPost('');
      setLocalEmbedPreview(null);
      setPostMedia(null);
      setPostMediaPreview(null);
      fetchPosts();
    } catch (err) {
      alert('Failed to create post: ' + err.message);
    } finally {
      setIsSubmittingPost(false);
    }
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPostMedia(file);
      setPostMediaPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveMedia = () => {
    setPostMedia(null);
    setPostMediaPreview(null);
  };

  // Load reposts, shares, comments counts (reactions come from server)
  useEffect(() => {
    async function loadAll() {
      const newReactions = {};
      const newUserReactions = {};
      const newReposts = {};
      const newShares = {};
      const newCommentCounts = {};
      for (const post of posts) {
        // Use server-provided reactions
        newReactions[post.id] = post.reactionsByType || {};
        newUserReactions[post.id] = post.userReactionType || null;
        newReposts[post.id] = await fetchRepostCount(post.id);
        newShares[post.id] = await fetchShareCount(post.id);
        // Fetch comment count for this post (accurate count)
        const { count: commentCount, error: commentCountError } = await supabase
          .from('comments')
          .select('id', { count: 'exact', head: true })
          .eq('post_id', post.id);
        newCommentCounts[post.id] = commentCountError ? 0 : commentCount;
      }
      setReactionsMap(newReactions);
      setUserReactions(newUserReactions);
      setRepostCounts(newReposts);
      setShareCounts(newShares);
      setCommentCounts(newCommentCounts);
    }
    if (posts && posts.length > 0) loadAll();
  }, [posts, userProfile.id, supabase, commentsRefreshKey]);

  // Picker close on outside mouse leave
  useEffect(() => {
    function handleMouseDown(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowReactions({});
      }
    }
    
    // Add event listener if any reaction picker is visible
    const isAnyPickerVisible = Object.values(showReactions).some(value => value);
    
    if (isAnyPickerVisible) {
      document.addEventListener('mousedown', handleMouseDown);
    } else {
      document.removeEventListener('mousedown', handleMouseDown);
    }
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [showReactions]);

  const handleReactionUpdate = async (postId, prevType, nextType) => {
    setUserReactions(prev => ({ ...prev, [postId]: nextType || undefined }));
    setReactionsMap(prev => {
      const curr = { ...(prev[postId] || {}) };
      if (prevType) curr[prevType] = Math.max(0, (curr[prevType] || 0) - 1);
      if (nextType) curr[nextType] = (curr[nextType] || 0) + 1;
      return { ...prev, [postId]: curr };
    });
    try {
      const reactions = await fetchReactions(postId);
      setReactionsMap(prev => ({
        ...prev,
        [postId]: reactions.reduce((acc, r) => {
          acc[r.reaction_type] = (acc[r.reaction_type] || 0) + 1;
          return acc;
        }, {})
      }));
    } catch {}
  };

  const handleRepost = async (post) => {
    try {
      await addRepost(post.id, userProfile.id);
      const count = await fetchRepostCount(post.id);
      setRepostCounts(prev => ({ ...prev, [post.id]: count }));
      alert('Reposted!');
    } catch (err) {
      alert('Failed to repost: ' + err.message);
    }
  };

  const handleShare = async (post) => {
    try {
      await addShare(post.id, userProfile.id);
      const count = await fetchShareCount(post.id);
      setShareCounts(prev => ({ ...prev, [post.id]: count }));
      const shareData = {
        title: 'Check out this post!',
        text: post.content,
        url: window.location.href
      };
      if (navigator.share) {
        navigator.share(shareData).catch(() => {});
      } else {
        alert('Share link copied!');
        navigator.clipboard.writeText(window.location.href);
      }
    } catch (err) {
      alert('Failed to share: ' + err.message);
    }
  };

  const handleEditPost = (post) => {
    setEditingPostId(post.id);
    setEditedPostContent(post.content);
    // If the post has media or embed, we might want to allow editing that too
    // For now, just focusing on content
  };

  const handleCancelEditPost = () => {
    setEditingPostId(null);
    setEditedPostContent('');
  };

  const handleSaveEditPost = async (postId) => {
    if (!editedPostContent.trim()) return;
    const { error } = await supabase
      .from('posts')
      .update({ content: editedPostContent })
      .eq('id', postId);
    if (!error) {
      setEditingPostId(null);
      setEditedPostContent('');
      fetchPosts(); // Refresh posts to show the update
    } else {
      alert('Failed to save post: ' + error.message);
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      // First, delete associated comments (if any)
      // This depends on your DB schema and cascade rules. If cascade delete is set up, this might not be needed.
      // await supabase.from('comments').delete().eq('post_id', postId);
      // Then, delete associated reactions (if any)
      // await supabase.from('post_reactions').delete().eq('post_id', postId);
      // Finally, delete the post
      const { error } = await supabase.from('posts').delete().eq('id', postId);
      if (!error) {
        fetchPosts(); // Refresh posts
      } else {
        alert('Failed to delete post: ' + error.message);
      }
    }
  };

  // Handler for DockedComposer submission
  const handleDockedSubmit = async (content, mediaFile, mediaType, embedPreview) => {
    const { content: cleanContent, embedUrl } = extractEmbedFromContent(content);
    const embed_url = embedUrl || null;
    if (!cleanContent && !mediaFile && !embed_url) return;
    
    setIsSubmittingPost(true);
    try {
      let uploadedUrl = null;
      let uploadedType = null;
      if (mediaFile) {
        const ext = mediaFile.name.split('.').pop();
        const filePath = `posts/${userProfile.id}/${Date.now()}_${Math.random().toString(36).substr(2, 8)}.${ext}`;
        let { error: uploadError } = await supabase.storage
          .from('post-media')
          .upload(filePath, mediaFile, { cacheControl: '3600', upsert: false });
        if (uploadError) {
          alert('Failed to upload media');
          throw new Error('Upload failed');
        }
        const { data: publicUrlData } = supabase
          .storage
          .from('post-media')
          .getPublicUrl(filePath);
        uploadedUrl = publicUrlData.publicUrl;
        uploadedType = mediaFile.type;
      }

      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token || '';
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };

      const res = await fetch('/api/community', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: cleanContent || '',
          groupId,
          media_url: uploadedUrl || undefined,
          media_type: uploadedType || undefined,
          embed_url: embed_url || undefined
        }),
      });

      if (!res.ok) throw new Error(`Post failed: ${res.status}`);
      fetchPosts();
    } catch (err) {
      alert('Failed to create post: ' + err.message);
      throw err;
    } finally {
      setIsSubmittingPost(false);
    }
  };

  const getAuthorMeta = (post) => {
    const isOwner = post.user_id === userProfile?.id;
    const name =
      post.profile?.display_name ||
      post.author?.display_name ||
      post.author?.name ||
      (isOwner ? userProfile?.display_name : null) ||
      'User';
    const avatar =
      post.profile?.avatar_url ||
      post.author?.avatar_url ||
      (isOwner ? userProfile?.avatar_url : null) ||
      '/default-avatar.png';
    return {
      name,
      avatar,
      date: post.created_at ? new Date(post.created_at).toLocaleString() : '',
    };
  };

  return (
    <div className="forum-tab social-card">
      <DockedComposer
        placeholder="Share something with the group..."
        onSubmit={handleDockedSubmit}
        isSubmitting={isSubmittingPost}
        title="Post to Group"
      />
      <div className="forum-posts-list social-posts-list">
        {posts.length === 0 && <div className="no-posts">No posts yet. Be the first to post!</div>}
        {posts.map((post) => (
          <div key={post.id} className={`forum-post${post.id === editingPostId ? ' editing' : ''} social-post-card`} id={`forum-post-${post.id}`}>
            <div className="forum-post-header social-post-header">
              <div className="social-author-block">
                <img
                  src={getAuthorMeta(post).avatar}
                  alt={getAuthorMeta(post).name}
                  className="forum-avatar social-avatar"
                />
                <div className="social-author-meta">
                  <HoverCard type="profile" data={post.profile || post.author || { id: post.user_id }} currentUserId={userProfile?.id}>
                    <a href={`/app/profile/${post.user_id}`} className="author-name-link">
                      <span className="forum-display-name social-author-name">{getAuthorMeta(post).name}</span>
                    </a>
                  </HoverCard>
                  <span className="forum-post-date social-post-date">{getAuthorMeta(post).date}</span>
                </div>
              </div>
              {userProfile && userProfile.id === post.user_id && (
                <div style={{ marginLeft: 'auto', position: 'relative' }}>
                  <button
                    type="button"
                    className="social-kebab-btn"
                    aria-label="Post options"
                    onClick={() =>
                      setOwnerMenuOpen(prev => ({
                        ...prev,
                        [post.id]: !prev[post.id]
                      }))
                    }
                  >
                    ⋮
                  </button>
                  {ownerMenuOpen[post.id] && (
                    <div className="social-kebab-menu">
                      <button
                        type="button"
                        className="social-kebab-item"
                        onClick={() => {
                          handleEditPost(post);
                          setOwnerMenuOpen(prev => ({ ...prev, [post.id]: false }));
                        }}
                      >
                        <FaEdit />
                        Edit post
                      </button>
                      <div className="social-kebab-danger-label">Danger zone</div>
                      <button
                        type="button"
                        className="social-kebab-item danger"
                        onClick={() => {
                          setOwnerMenuOpen(prev => ({ ...prev, [post.id]: false }));
                          handleDeletePost(post.id);
                        }}
                      >
                        <FaTrash />
                        Delete post
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {editingPostId === post.id ? (
              <div className="forum-post-edit-area">
                <textarea
                  value={editedPostContent}
                  onChange={(e) => setEditedPostContent(e.target.value)}
                  className="forum-post-edit-textarea"
                />
                <div className="forum-post-edit-actions">
                  <button onClick={() => handleSaveEditPost(post.id)} className="save-edit-btn">
                    <FaSave /> Save
                  </button>
                  <button onClick={handleCancelEditPost} className="cancel-edit-btn">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="forum-post-content social-post-content">
                <span>{post.content}</span>
              </div>
            )}
            {(post.media_url && post.media_type) && (
              <div className="forum-post-media social-post-media">
                {post.media_type.startsWith('image') ? (
                  <img src={post.media_url} alt="media" />
                ) : post.media_type.startsWith('video') ? (
                  <video src={post.media_url} controls />
                ) : post.media_type.startsWith('audio') ? (
                  <audio src={post.media_url} controls />
                ) : null}
              </div>
            )}
            {post.embed_url && (() => {
              const platform = getEmbedPlatform(post.embed_url);
              const menuKey = `embed-${post.id}`;
              return (
                <div className="forum-post-embed social-post-media" style={{ position: 'relative' }}>
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                    <iframe
                      src={getEmbedSrc(post.embed_url)}
                      title="Embedded media"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                      allowFullScreen
                    />
                  </div>
                  {platform !== 'YouTube' && (
                  <div style={{ position: 'absolute', top: 8, right: 8 }} ref={embedMenuOpen[menuKey] ? embedMenuRef : null}>
                    <button
                      className="icon-btn"
                      onClick={() => toggleEmbedMenu(menuKey)}
                      title="Options"
                      style={{ background: 'rgba(0,0,0,0.6)', padding: '8px' }}
                    >
                      ⋮
                    </button>
                    {embedMenuOpen[menuKey] && (
                      <div className="embed-menu-dropdown" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'rgba(20,24,36,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', minWidth: 180, zIndex: 1000 }}>
                        <button
                          onClick={() => copyEmbedLink(post.embed_url)}
                          style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <FaLink /> Copy Link
                        </button>
                        <button
                          onClick={() => viewOnPlatform(post.embed_url)}
                          style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: '#f4f6ff' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,77,255,0.2)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <FaExternalLinkAlt /> View on {platform}
                        </button>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              );
            })()}
            <div className="forum-post-actions-bar social-post-actions">
              <PostReactions
                postId={post.id}
                stats={{
                  reactionsByType: reactionsMap[post.id] || {},
                  reactionsCount: Object.values(reactionsMap[post.id] || {}).reduce((a, b) => a + b, 0),
                  userReactionType: userReactions[post.id] || null,
                }}
                getAuthHeaders={getAuthHeaders}
                onUpdate={(prev, next) => handleReactionUpdate(post.id, prev, next)}
              />
              <button
                className="forum-action-btn"
                title="Repost"
                onClick={() => handleRepost(post)}
              >
                <FaRetweet /> {repostCounts[post.id] || 0}
              </button>
              <button
                type="button"
                className="forum-action-btn"
                title="Comments"
                onClick={() => {
                  setActiveCommentPostId(post.id);
                  setCommentModalOpen(true);
                }}
              >
                <FaRegCommentDots />
                <span style={{ marginLeft: 4, fontSize: 14 }}>{commentCounts[post.id] || 0}</span>
              </button>
              <button
                className="forum-action-btn"
                title="Share"
                onClick={() => handleShare(post)}
              >
                <FaShareAlt /> {shareCounts[post.id] || 0}
              </button>
            </div>
            <div className="social-post-stats">
              <span>{Object.values(reactionsMap[post.id] || {}).reduce((a, b) => a + b, 0)} reactions</span>
              <span>{commentCounts[post.id] || 0} comments</span>
              <span>{repostCounts[post.id] || 0} reposts</span>
              <span>{shareCounts[post.id] || 0} shares</span>
            </div>
            <Modal
              isOpen={commentModalOpen && activeCommentPostId === post.id}
              onRequestClose={() => setCommentModalOpen(false)}
              ariaHideApp={false}
              className="forum-comment-modal"
              overlayClassName="forum-comment-modal-overlay"
            >
              <button onClick={() => setCommentModalOpen(false)} className="close-modal-x-btn" aria-label="Close modal">&#10005;</button>
              <Comments
                postId={post.id}
                user={userProfile}
                onCommentAdded={() => setCommentsRefreshKey(k => k + 1)}
                replyTo={replyToCommentId}
                openReplyModal={id => setReplyToCommentId(id)}
              />
            </Modal>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ForumTab;